/**
 * #987 — which Polymarket US line a Game Detail row stands for.
 *
 * Moneyline rows join the game's moneyline; synthesized spread rows join the
 * exact full-game spread through their retained favored side and line
 * (`spreadOutcome`); synthesized totals join the exact native full-game total
 * through their typed threshold (`totalOutcome`, #1030); native player and
 * game props retain their exact targets.
 * Other contracts remain unsupported here rather than being joined to the
 * moneyline. Producer publication alone does not make a UI target supported.
 */
import { usGameKeyForBoardKey, type UsLineTarget } from '@/lib/polymarketUs/gameMapping';
import { usGameSportForKey, type UsPeriod } from '@/lib/polymarketUs/sports';
import { usFullGameTotalFamilyForSport, validUsTotalThreshold } from '@/lib/polymarketUs/totalTarget';
import { baseGameKey, orderScopeGameKey } from '@/services/analyticsGameKey';
import { gameMarketKind } from '../tradeModalBook';
import type { GameRow } from '../types';

export function usLineTargetFor(game: GameRow): UsLineTarget {
  if (game.usGameProp && game.key === `${orderScopeGameKey(game.key)}|us-game-prop|${game.usGameProp.marketSlug}`)
    return { kind: 'game-prop', target: game.usGameProp };
  if (game.usPlayerProp && game.key === `${orderScopeGameKey(game.key)}|us-player|${game.usPlayerProp.marketSlug}`)
    return { kind: 'player-prop', target: game.usPlayerProp };
  const kind = gameMarketKind(game);
  if (kind === 'moneyline') {
    const period = /\|(1H|2H|[1-4]Q)-ml$/.exec(game.key);
    if (period && game.key.slice(0, period.index) === orderScopeGameKey(game.key)) {
      return { kind: 'moneyline', period: period[1] as UsPeriod };
    }
    // Period, tie, BTTS and event rows are distinct contracts. The broad
    // modal classifier calls some of them moneylines; US must not substitute
    // the parent game's full-game moneyline for the selected contract.
    return game.key.split('|').length === 5 && orderScopeGameKey(game.key) === baseGameKey(game.key)
      ? { kind: 'moneyline' }
      : { kind: 'unsupported' };
  }
  if (kind === 'total') return totalTargetFor(game);
  if (kind !== 'spread' || !game.spreadOutcome || !game.awayCode || !game.homeCode) return { kind: 'unsupported' };
  const { favored, line } = game.spreadOutcome;
  return {
    kind: 'spread',
    favoredCode: favored === 'away' ? game.awayCode : game.homeCode,
    otherCode: favored === 'away' ? game.homeCode : game.awayCode,
    magnitude: Math.abs(line),
  };
}

/**
 * #1030 — a synthesized total joins the exact native full-game total: the
 * row's typed threshold (`totalOutcome`, never its "Over N" name or a ticker),
 * a full-game period stated by the row's source, and the one family supported
 * for its US sport. A period, unknown-period, missing or unrepresentable
 * threshold, or unsupported sport stays `unsupported`.
 */
function totalTargetFor(game: GameRow): UsLineTarget {
  const outcome = game.totalOutcome;
  const base = baseGameKey(game.key);
  const usKey = usGameKeyForBoardKey(base);
  const family = usFullGameTotalFamilyForSport(usGameSportForKey(usKey));
  if (!outcome || outcome.period !== 'full' || !validUsTotalThreshold(outcome.line) || !family
    || base.split('|').length !== 5 || orderScopeGameKey(game.key) !== base || !game.key.startsWith(`${base}|total|`))
    return { kind: 'unsupported' };
  return { kind: 'total', family, threshold: outcome.line };
}

/** Stable identity for keyed results. */
export function usLineTargetKey(target: UsLineTarget): string {
  if (target.kind === 'game-prop') return `game-prop:${JSON.stringify(target.target)}`;
  if (target.kind === 'player-prop') return `player-prop:${JSON.stringify(target.target)}`;
  if (target.kind === 'total') return `total:${target.family}:${target.threshold}`;
  return target.kind === 'spread' ? `spread:${target.favoredCode}:${target.magnitude}`
    : target.kind === 'moneyline' ? `moneyline:${target.period ?? 'full'}` : target.kind;
}
